/**
 * FeesSection — the Agreement Fees summary (per-account ROLLUP).
 *
 * Fee Schedule model / redesign §10 (Slice 5 compute cutover): a READ-ONLY summary
 * of how the agreement's accounts are billed — which fee schedule(s) they're on,
 * their billing frequency and VAT, and how many accounts are not yet assigned (under
 * NO-BACKFILL an unassigned account produces no fee, so it is surfaced loudly).
 *
 * ALL per-account editing lives on the Billing-details rows below
 * (BillingIdentitySection): schedule + frequency + VAT are set there, per account.
 * This card no longer sets an agreement-level fee config — the old
 * agreement_fee_config authoring (basis/frequency/VAT + Standard-copy/Custom
 * tranches) is retired with the compute cutover. It reads the SAME
 * ['account-billing', id] list the rows use, so it stays in sync for free.
 */
import { useMemo } from 'react';
import { Alert, Badge, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconReceipt2 } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';

import { accountBillingApi, type BillingIdentityRow } from '../api/accountBilling';
import { bpsToPct } from '../api/feeSchedules';

const FREQ_LABEL: Record<string, string> = {
  monthly: 'Monthly', quarterly: 'Quarterly', semiannual: 'Semiannual',
};

interface SchedGroup {
  name: string;
  count: number;
  freq: string | null;   // the group's frequency label, or "mixed frequency", or null
}

interface Rollup {
  headline: string;
  groups: SchedGroup[];
  mixed: boolean;        // more than one distinct schedule in use
  vatLine: string;
  unassigned: number;
}

/** Summarise the per-account billing rows into the Fees rollup (redesign §10). */
function summarise(rows: BillingIdentityRow[]): Rollup {
  const total = rows.length;
  const assigned = rows.filter((r) => r.fee_schedule_id != null);
  const unassigned = total - assigned.length;

  // Group assigned accounts by schedule name (+ collect their frequencies).
  const byName = new Map<string, { count: number; freqs: Set<string> }>();
  for (const r of assigned) {
    const name = r.fee_schedule_name ?? '(unnamed)';
    const g = byName.get(name) ?? { count: 0, freqs: new Set<string>() };
    g.count += 1;
    if (r.fee_frequency) g.freqs.add(r.fee_frequency);
    byName.set(name, g);
  }
  const groups: SchedGroup[] = [...byName.entries()]
    .map(([name, g]) => {
      const f = [...g.freqs];
      const freq = f.length === 1 ? (FREQ_LABEL[f[0]] ?? f[0]) : (f.length > 1 ? 'mixed frequency' : null);
      return { name, count: g.count, freq };
    })
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));

  // VAT summary across assigned accounts.
  const vats = [...new Set(
    assigned.map((r) => r.vat_rate_bps).filter((v): v is number => v != null),
  )].sort((a, b) => a - b);
  const vatLine =
    vats.length === 0 ? 'VAT not set'
      : vats.length === 1 ? `VAT ${bpsToPct(vats[0]).toFixed(2)}%`
        : `VAT varies (${vats.map((v) => `${bpsToPct(v).toFixed(2)}%`).join(', ')})`;

  const mixed = groups.length > 1;
  let headline: string;
  if (assigned.length === 0) {
    headline = `${total} account${total === 1 ? '' : 's'} · none assigned a fee schedule yet`;
  } else if (!mixed && unassigned === 0) {
    const g = groups[0];   // uniform: one schedule, every account on it
    headline = `All ${total} account${total === 1 ? '' : 's'} · ${g.name}${g.freq ? ` (${g.freq})` : ''}`;
  } else {
    headline = groups.map((g) => `${g.count} × ${g.name}`).join(' · ');
  }

  return { headline, groups, mixed, vatLine, unassigned };
}

export function FeesSection({ agreementId }: { agreementId: string }) {
  // Same query key + fetch as BillingIdentitySection — shared cache, always in sync.
  const q = useQuery({
    queryKey: ['account-billing', agreementId],
    queryFn: () => accountBillingApi.list(agreementId),
  });
  const rows = q.data ?? [];
  const roll = useMemo(() => summarise(rows), [rows]);

  return (
    <Stack>
      <Group gap="xs"><IconReceipt2 size={20} /><Title order={4}>Fees</Title></Group>

      {q.isLoading && <Text c="dimmed">Loading…</Text>}

      {!q.isLoading && rows.length === 0 && (
        <Alert color="gray" variant="light">
          No accounts are related to this agreement yet — nothing to bill. A billing
          row appears once an account is linked (directly or via an insurance policy).
        </Alert>
      )}

      {!q.isLoading && rows.length > 0 && (
        <Paper withBorder p="md" radius="md" maw={620}>
          <Stack gap="xs">
            <Text fw={600}>{roll.headline}</Text>

            {roll.mixed && (
              <Stack gap={4}>
                {roll.groups.map((g) => (
                  <Group key={g.name} gap="xs">
                    <Badge variant="light" color="blue">{g.count}</Badge>
                    <Text size="sm">{g.name}{g.freq ? ` · ${g.freq}` : ''}</Text>
                  </Group>
                ))}
              </Stack>
            )}

            <Text size="sm" c="dimmed">{roll.vatLine}</Text>

            {roll.unassigned > 0 && (
              <Alert color="orange" variant="light">
                {roll.unassigned} account{roll.unassigned === 1 ? '' : 's'} not assigned a fee
                schedule — {roll.unassigned === 1 ? 'it is' : 'they are'} not billed. Assign a
                schedule in <b>Billing details</b> below.
              </Alert>
            )}

            <Text size="xs" c="dimmed">
              Fee schedule, frequency and VAT are set per account in Billing details below.
            </Text>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
